import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';
import { saveAs } from 'file-saver';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Table, Tooltip, Flex, notification, Button, Drawer, Input, Select, DatePicker, Modal, Form } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    ReloadOutlined,
    TeamOutlined,
    PhoneFilled,
    WarningOutlined,
    HistoryOutlined,
    FileExcelOutlined,
} from '@ant-design/icons';

import CallLogModal from '@components/users/call-log-modal';
import CallLogHistory from '@components/users/call-log-history';

import Processing from '@controls/processing';
import Filter from '@controls/filter/filter';
import Toolbar from '@controls/toolbar/toolbar';
import IBaseComponentProps from '@components/base-component-props';

import { userLoaded, setFilter } from '@store/actions';

import { securityRestriction, exception } from '@extensions/notification';
import { getDate, getDateTimeLocal, delayAction, dataURItoBlob, getEnumList } from '@extensions/utils';

import { useAppDispatch, useAppSelector } from '@store/hooks';

import { IUserReport } from '@src/core/entities/user-report';
import { IUserReportFilter } from '@src/core/entities/user-report-filter';
import { IUserSession } from '@entities/user-session';
import { ICity } from '@entities/city';

import { Permission, hasPermission } from '@enums/permission';
import { PeriodType, enumLabel as PeriodLabel } from '@enums/period-type';
import { IEnumItem } from '@enums/enum-item';

import { EmailIcon, MaxAppIcon, MobileIcon, TelegramIcon } from '@core/icons';

dayjs.extend(utc);

const MaxDaysQty: number = 14;
const MaxCallDaysQty: number = 7;

const filterContext: string = 'UsersReport';

const UsersReport = (props: IBaseComponentProps) => {
    const {modulePath, serverFetch } = props;

    const { RangePicker } = DatePicker;

    const [exportForm] = Form.useForm();

    const initFilter: IUserReportFilter = {
        userLogin: undefined,
        cityIds: [],
    };

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const d = useAppDispatch();

    const [api, contextHolder] = notification.useNotification();

    const [users, setUsers] = useState<Array<IUserReport>>([]);
    const [currentUser, setCurrentUser] = useState<IUserReport>();
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false);

    const filter = useAppSelector<IUserReportFilter>((s) => s.filters[filterContext]);

    const [openAddCallLog, setAddOpenCallLog] = useState<boolean>(false);
    const [openLogHistory, setOpenLogHistory] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [openExportModal, setOpenExportModal] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);
    const [periodTypes] = useState<Array<IEnumItem>>(getEnumList(PeriodType, PeriodLabel));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            const promises = [
                await serverFetch('cities', { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCities(result[0][0] || []);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        const fetchData = async () => {
            setLoadingUsers(true);

            setUsers([]);

            const promises = [
                await serverFetch('reports/users', { method: 'GET', queryParams: filter })
                    .then((data: Array<IUserReport>) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения отчета по клиентам', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setUsers(result[0][0] || []);

                setLoadingUsers(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onExport = (data: any) => {
        var exportFilter: IUserReportFilter = { ...filter };
        exportFilter.periodType = data.periodType;

        setOpenExportModal(false);
        setExporting(true);

        serverFetch('reports/volume/export', { method: 'GET', queryParams: exportFilter })
            .then((data: any) => {
                setExporting(false);

                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;
                saveAs(dataURItoBlob(fileBody), data.fileDownloadName);
            })
            .catch((ex: any) => {
                setExporting(false);
                exception(api, 'Ошибка выгрузки статистики по заказам', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        disabled: loadingUsers,
                        key: 'refresh',
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Выгрузить статистику по заказам в Excel',
                        key: 'exportToExcel',
                        icon: <FileExcelOutlined />,
                        onClick: () => setOpenExportModal(true),
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <Filter
                display={showFilter}
                items={[
                    <Input
                        style={{ width: 200 }}
                        key='userLogin'
                        placeholder='Логин'
                        value={filter?.userLogin}
                        onChange={(data) => {
                            d(setFilter({ ...filter, userLogin: data.target.value }, filterContext));
                        }}
                    />,
                    <Select
                        key='city'
                        placeholder='Город доставки'
                        value={filter?.cityIds}
                        size='middle'
                        mode='multiple'
                        maxTagCount='responsive'
                        allowClear
                        style={{ width: 250 }}
                        onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                        showSearch={{
                            filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                            filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                        }}
                        options={cities.map((s) => {
                            return { value: s.id, label: s.name };
                        })}
                    />,
                    <RangePicker
                        style={{ width: 220 }}
                        allowEmpty={[true, true]}
                        key='departure'
                        format='DD.MM.YYYY'
                        placeholder={['Заказ с', 'до']}
                        cellRender={(current) => {
                            return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                        }}
                        value={[
                            filter?.loadingFrom ? dayjs(filter?.loadingFrom) : null,
                            filter?.loadingTo ? dayjs(filter?.loadingTo) : null,
                        ]}
                        onChange={(value) => {
                            if (!value) {
                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            loadingFrom: undefined,
                                            loadingTo: undefined,
                                        },
                                        filterContext,
                                    ),
                                );

                                return;
                            }

                            d(
                                setFilter(
                                    {
                                        ...filter,
                                        loadingFrom: value[0]
                                            ? dayjs(value[0]).utc(true).startOf('day').toString()
                                            : undefined,
                                        loadingTo: value[1]
                                            ? dayjs(value[1]).utc(true).endOf('day').toString()
                                            : undefined,
                                    },
                                    filterContext,
                                ),
                            );
                        }}
                    />,
                ]}
                onClear={() => d(setFilter(initFilter, filterContext))}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IUserReport> = [
            {
                title: 'Логин',
                width: 120,
                render: (_, record) => {
                    return (
                        <a
                            href={`${window.location.protocol}//${window.location.host}/${modulePath}/settings/users/${record?.userId}`}
                            target='_blank'
                        >
                            {record.userLogin}
                        </a>
                    );
                },
            },
            {
                title: 'Город',
                width: 200,
                dataIndex: 'cityName',
            },
            {
                dataIndex: 'warning',
                width: 40,
                onCell: () => ({
                    style: {
                        alignContent: 'center',
                    },
                }),
                render: (_: any, record: IUserReport) => {
                    return (
                        (record.callDaysQty == null || record.callDaysQty > MaxCallDaysQty) && (
                            <Tooltip
                                title={
                                    record.lastCallOn ? `Последний звонок был ${getDateTimeLocal(record.lastCallOn)}` : 'Клиенту не звонили'
                                }
                                color='var(--main-red)'
                            >
                                <Flex vertical style={{ height: '100%' }}>
                                    <WarningOutlined style={{ fontSize: 20, color: 'var(--main-red)' }} />
                                </Flex>
                            </Tooltip>
                        )
                    );
                },
            },
            {
                dataIndex: 'addCallLog',
                width: 40,
                render: (_: any, record: IUserReport) => {
                    return (
                        <Tooltip title={`Позвонить клиенту`} color='var(--main-green)'>
                            <Button
                                size='small'
                                icon={<PhoneFilled style={{ color: '#FFFFFF' }} />}
                                style={{ background: 'var(--main-green)' }}
                                onClick={() => {
                                    if (!hasPermission(userSession.permissions, Permission.ManageUserCallLog)) {
                                        securityRestriction(api, [Permission.ManageUserCallLog]);
                                        return;
                                    }

                                    setCurrentUser(record);
                                    setAddOpenCallLog(true);
                                }}
                            />
                        </Tooltip>
                    );
                },
            },
            {
                dataIndex: 'logHistory',
                width: 40,
                render: (_: any, record: IUserReport) => {
                    return (
                        <Tooltip placement='topLeft' title='История звонков'>
                            <Button
                                icon={<HistoryOutlined style={{ fontSize: 16 }} />}
                                size='small'
                                onClick={() => {
                                    if (!hasPermission(userSession.permissions, Permission.ManageUserCallLog)) {
                                        securityRestriction(api, [Permission.ManageUserCallLog]);
                                        return;
                                    }

                                    setCurrentUser(record);
                                    setOpenLogHistory(true);
                                }}
                            />
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Телефон',
                dataIndex: 'phone',
                width: 150,
                render: (_, record) => {
                    return (
                        <Flex vertical style={{ height: 40 }}>
                            <div>{record.phone}</div>
                            {record.phone2 && <div>{record.phone2}</div>}
                        </Flex>
                    );
                },
            },
            {
                width: 120,
                align: 'center',
                render: (_, record) => {
                    return (
                        <Flex gap='small'>
                            <Tooltip
                                title={`Приложение "My Freshline" ${record.hasMobileApp ? 'используется' : 'не используется'}.`}
                                color={record.hasMobileApp ? 'var(--main-green)' : 'var(--main-red)'}
                            >
                                <div>
                                    <MobileIcon
                                        style={{ color: record.hasMobileApp ? 'var(--main-green)' : 'var(--main-red)', fontSize: 20 }}
                                    />
                                </div>
                            </Tooltip>
                            <Tooltip
                                title={`Email рассылка ${record.hasEmail ? 'активна' : 'не активна'}.`}
                                color={record.hasEmail ? 'var(--main-green)' : 'var(--main-red)'}
                            >
                                <div>
                                    <EmailIcon style={{ color: record.hasEmail ? 'var(--main-green)' : 'var(--main-red)', fontSize: 20 }} />
                                </div>
                            </Tooltip>
                            <Tooltip
                                title={`Telegram Bot ${record.hasTelegram ? 'активен' : 'не активен'}.`}
                                color={record.hasTelegram ? 'var(--main-green)' : 'var(--main-red)'}
                            >
                                <div>
                                    <TelegramIcon
                                        style={{ color: record.hasTelegram ? 'var(--main-green)' : 'var(--main-red)', fontSize: 20 }}
                                    />
                                </div>
                            </Tooltip>
                            <Tooltip
                                title={`Max Bot ${record.hasMax ? 'активен' : 'не активен'}.`}
                                color={record.hasMax ? 'var(--main-green)' : 'var(--main-red)'}
                            >
                                <div>
                                    <MaxAppIcon style={{ color: record.hasMax ? 'var(--main-green)' : 'var(--main-red)', fontSize: 20 }} />
                                </div>
                            </Tooltip>
                        </Flex>
                    );
                },
            },
            {
                title: 'Email',
                dataIndex: 'email',
                width: 250,
            },
            {
                title: 'Дней с поседнего заказа',
                width: 180,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                        color: '#FFFFFF',
                        background: record.daysQty < MaxDaysQty ? 'var(--main-green)' : 'var(--main-red)',
                    },
                }),
                render: (_: any, record: IUserReport) => {
                    return record.daysQty;
                },
            },
            {
                title: 'Дата поседнего заказа',
                width: 180,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                        color: '#FFFFFF',
                        background: record.daysQty < MaxDaysQty ? 'var(--main-green)' : 'var(--main-red)',
                    },
                }),
                render: (_: any, record: IUserReport) => {
                    return getDate(record.lastLoadingOn);
                },
            },
            {
                title: 'Поседний звонок',
                width: 180,
                align: 'center',
                render: (_: any, record: IUserReport) => {
                    return getDateTimeLocal(record.lastCallOn);
                },
            },

            {
                title: 'Менеджер',
                width: 250,
                align: 'center',
                dataIndex: 'lastCallBy',
            },
            {},
        ];

        return (
            <Table
                virtual={true}
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={users}
                loading={{
                    spinning: loadingUsers,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                scroll={{ y: height ?? 0 }}
                footer={() =>
                    users && (
                        <Flex align='center' justify='start' gap='large' style={{ fontSize: 16, fontWeight: 600 }}>
                            <Tooltip title={`Последний заказ более ${MaxDaysQty} дней назад`} color='var(--main-red)'>
                                <TeamOutlined style={{ fontSize: 20, marginRight: 5, color: 'var(--main-red)' }} />
                                <span style={{ color: 'var(--main-red)' }}>{users.filter((u) => u.daysQty >= MaxDaysQty).length}</span>
                            </Tooltip>

                            <Tooltip title={`Последний заказ менее ${MaxDaysQty} дней назад`} color='var(--main-green)'>
                                <TeamOutlined style={{ fontSize: 20, marginRight: 5, color: 'var(--main-green)' }} />
                                <span style={{ color: 'var(--main-green)' }}>{users.filter((u) => u.daysQty < MaxDaysQty).length}</span>
                            </Tooltip>
                        </Flex>
                    )
                }
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 290px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {currentUser && openAddCallLog && (
                <CallLogModal
                    userId={currentUser.userId}
                    serverFetch={serverFetch}
                    api={api}
                    onSave={() => {
                        setCurrentUser(undefined);
                        setAddOpenCallLog(false);
                        setRefreshRequired(true);
                    }}
                    onCancel={() => {
                        setCurrentUser(undefined);
                        setAddOpenCallLog(false);
                    }}
                />
            )}

            <Drawer
                title={`История звонков "${currentUser?.userName}"`}
                placement='right'
                closable={true}
                styles={{
                    body: {
                        padding: 15,
                    },
                }}
                destroyOnHidden={true}
                size={900}
                onClose={() => {
                    setCurrentUser(undefined);
                    setOpenLogHistory(false);
                }}
                open={!!currentUser && openLogHistory}
            >
                <CallLogHistory userId={currentUser?.userId} serverFetch={serverFetch} api={api} />
            </Drawer>

            <Modal
                destroyOnHidden
                width={400}
                title='Отчет по заказам'
                open={openExportModal}
                okText='ОК'
                closable={false}
                okButtonProps={{ disabled: exporting }}
                onOk={() => {
                    exportForm.submit();
                }}
                onCancel={() => {
                    exportForm.resetFields();
                    setOpenExportModal(false);
                }}
            >
                <Form colon={false} wrapperCol={{ span: 24 }} form={exportForm} onFinish={onExport}>
                    <Form.Item
                        label='Группировка'
                        name='periodType'
                        required
                        rules={[{ required: true, message: 'Укажите тип группировки' }]}
                    >
                        <Select
                            size='middle'
                            style={{ width: 200 }}
                            options={periodTypes.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {exporting && <Processing title='Выгрузка статистики по заказам...' />}

            {contextHolder}
        </div>
    );
};

export default UsersReport;
